import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PricingFeatureMatrix } from "../../components/PricingFeatureMatrix";
import { MpCheckoutModal } from "../../components/MpCheckoutModal";
import { useLang } from "../../contexts/LangContext";
import { useAuth } from "../../contexts/AuthContext";
import { useSpace } from "../../contexts/SpaceContext";
import { billingService } from "../../services/billingService";
import { formatMoney } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, Package2, CreditCard, Gauge, Calendar, MessageSquare, Plus, Layers, Info, ShieldCheck, Check, Users } from "lucide-react";
import { PageHeader } from "../../components/PageHeader";

const FEATURE_LABELS = {
  advisor_chat:        { es: "Chat IA financiero",        en: "AI advisor chat" },
  telegram_bot:        { es: "Bot de Telegram",           en: "Telegram bot" },
  whatsapp_bot:        { es: "Bot de WhatsApp",           en: "WhatsApp bot" },
  weekly_digest:       { es: "Resumen semanal",           en: "Weekly digest" },
  fx_alerts:           { es: "Alertas de tipo de cambio", en: "FX drift alerts" },
  recurring_movements: { es: "Movimientos recurrentes",   en: "Recurring movements" },
  pdf_reports:         { es: "Reportes PDF",              en: "PDF reports" },
  calendar_heatmap:    { es: "Calendario heat-map",       en: "Calendar heat-map" },
  ai_recommendations:  { es: "Recomendaciones IA",        en: "AI recommendations" },
  cash_flow_weekly:    { es: "Flujo de caja semanal",     en: "Weekly cash-flow" },
  basic_reports:       { es: "Reportes básicos",          en: "Basic reports" },
  advanced_reports:    { es: "Reportes avanzados",        en: "Advanced reports" },
  scenarios:           { es: "Escenarios financieros",    en: "Financial scenarios" },
  smart_alerts:        { es: "Alertas inteligentes",      en: "Smart alerts" },
  amortization:        { es: "Amortización y deudas",     en: "Amortization & debts" },
  roles_permissions:   { es: "Roles y permisos",          en: "Roles & permissions" },
  accountant_readonly: { es: "Link solo lectura contador", en: "Read-only accountant link" },
  advanced_exports:    { es: "Exportaciones avanzadas",   en: "Advanced exports" },
  basic_api:           { es: "API básica",                en: "Basic API" },
  audit_log:           { es: "Auditoría / historial",     en: "Audit log" },
  whatsapp_record_movements: { es: "Registrar movs por WhatsApp", en: "Record movements via WhatsApp" },
  whatsapp_query_cash: { es: "Consultar caja por WhatsApp", en: "Query cash via WhatsApp" },
  whatsapp_alerts:     { es: "Alertas por WhatsApp",      en: "WhatsApp alerts" },
  image_extractions:   { es: "Recibos IA",                en: "AI Receipts" },
};
const RESOURCE_LABELS = {
  ai_messages_per_month: { es: "Mensajes IA / mes",    en: "AI messages / month",   unit: "msg" },
  connected_bots:        { es: "Bots conectados",      en: "Connected bots",        unit: "" },
  cash_accounts:         { es: "Cuentas financieras",  en: "Financial accounts",    unit: "" },
  pdf_exports_per_month: { es: "Exportes PDF / mes",   en: "PDF exports / month",   unit: "" },
  active_goals:          { es: "Metas activas",        en: "Active goals",          unit: "" },
  investments_tracked:   { es: "Inversiones seguidas", en: "Investments tracked",   unit: "" },
  users:                 { es: "Usuarios incluidos",   en: "Included users",        unit: "" },
  spaces:                { es: "Espacios financieros", en: "Financial spaces",      unit: "" },
  retention_days:        { es: "Retención (días)",     en: "Retention (days)",      unit: "d" },
  whatsapp_lines:        { es: "Líneas WhatsApp",      en: "WhatsApp lines",        unit: "" },
  // iter-169w — Comprobantes IA (OCR de recibos). El backend usa
  // `image_extractions_monthly` como resource key; este map lo
  // traduce a un nombre legible para el usuario.
  image_extractions_monthly: {
    es: "Recibo IA / mes", en: "AI Receipt / month", unit: "",
  },
};
const SERVICE_LABELS = {
  email_support:       { es: "Soporte por email",     en: "Email support" },
  priority_support:    { es: "Soporte prioritario",   en: "Priority support" },
  onboarding_1on1:     { es: "Onboarding 1:1",        en: "1:1 onboarding" },
  white_label:         { es: "Marca blanca",          en: "White label" },
  custom_integrations: { es: "Integraciones a medida", en: "Custom integrations" },
  data_export:         { es: "Exportación de datos",   en: "Data export" },
};

/** User-facing pricing page (Composition flow):
 *  1. Pick ONE primary plan (mandatory).
 *  2. Optionally toggle compatible secondary plans.
 *  3. Checkout → /api/billing/composition/start  (or /update if active).
 */
export default function Pricing() {
  const { lang } = useLang();
  const { user } = useAuth();
  const { spaces, activeSpaceId } = useSpace();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const highlightedRef = useRef(null);
  const [highlightedPlanId, setHighlightedPlanId] = useState(highlightId);
  const isEs = lang !== "en";
  const qc = useQueryClient();
  const [country, setCountry] = useState(null);
  const [selectedPrimary, setSelectedPrimary] = useState(null);
  const [selectedSecondaries, setSelectedSecondaries] = useState([]);

  // iter-170an — Per-workspace billing. When the user belongs to ≥2
  // workspaces, surface a "¿Para qué workspace?" selector above the
  // plans grid. Defaults to the active space; the chosen value is
  // sent as ``space_id`` to /composition/start|update and threaded
  // into the pack-checkout URL so the purchase associates with the
  // workspace the user explicitly picked.
  const showSpaceSelector = (spaces?.length || 0) >= 2;
  const [selectedSpaceId, setSelectedSpaceId] = useState(activeSpaceId);
  // When the SpaceContext finishes loading and activeSpaceId becomes
  // available, sync once at render time (no effect → keeps the
  // set-state-in-effect rule quiet). After the user explicitly picks
  // a space, selectedSpaceId stays put and won't be overwritten.
  if (activeSpaceId && !selectedSpaceId) {
    setSelectedSpaceId(activeSpaceId);
  }
  const selectedSpace = (spaces || []).find((s) => s.id === selectedSpaceId) || null;

  // In-app MercadoPago checkout state. We open MP in a popup window
  // (iframe is blocked by MP's X-Frame-Options) and poll
  // /billing/composition/me until the webhook flips status=active.
  // Shape: { initPoint, compositionId } or null.
  const [mpCheckout, setMpCheckout] = useState(null);

  // Billing period — sticky across /pricing ↔ /billing via localStorage
  // so a user who picked "Anual" on the marketing page lands here with
  // annual pre-selected. Resets cleanly to "monthly" if storage blocked.
  const BILLING_PERIOD_KEY = "lumfi.billing_period.v1";
  const [billingPeriod, setBillingPeriodState] = useState(() => {
    try {
      const v = localStorage.getItem(BILLING_PERIOD_KEY);
      return v === "annual" ? "annual" : "monthly";
    } catch { return "monthly"; }
  });
  const setBillingPeriod = (v) => {
    setBillingPeriodState(v);
    try { localStorage.setItem(BILLING_PERIOD_KEY, v); } catch { /* noop */ }
    if (v === "annual") {
      // Annual + addons NOW supported (one preapproval, addons sin
      // pricing_annual usan fallback monthly × 12). Mantener selección.
    }
  };

  const { data: countriesData } = useQuery({ queryKey: ["billing-countries"], queryFn: () => billingService.countries() });
  const supportedCodes = (countriesData?.countries || []).map((c) => c.code);
  const { data: meBilling } = useQuery({ queryKey: ["billing-me"], queryFn: () => billingService.me() });
  // The country the BACKEND uses for this user (drives MP currency +
  // catalog filtering). Visible to the user via the readout below the
  // toggle so they spot when it drifts from what they expected.
  const profileCountry = meBilling?.profile?.country || null;
  // The country shown in the UI — defaults to the profile's, falls
  // back to the first supported country. The dropdown above the grid
  // lets the user preview prices for other markets without committing.
  const activeCountry = country || profileCountry || supportedCodes[0] || "CO";

  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ["pricing-plans", activeCountry],
    queryFn: () => billingService.plans(activeCountry),
    enabled: !!activeCountry,
  });
  const { data: packsData, isLoading: packsLoading } = useQuery({
    queryKey: ["pricing-packs", activeCountry],
    queryFn: () => billingService.packs(activeCountry),
    enabled: !!activeCountry,
  });
  const { data: compMe } = useQuery({ queryKey: ["composition-me"], queryFn: () => billingService.compositionMe() });

  // IMPORTANT: memoize derived arrays so their reference is stable across
  // renders. Without this, `secondaries` would get a fresh `[]` on every
  // render and trigger the auto-prune useEffect below in an infinite loop
  // ("Maximum update depth exceeded").
  const plans = useMemo(() => plansData?.plans || [], [plansData]);
  const packs = useMemo(() => packsData?.packs || [], [packsData]);
  const composition = compMe?.composition || null;
  const hasComposition = composition && (composition.status === "active" || composition.status === "pending");

  // Initial state: when composition exists, pre-select user's current bundle.
  useEffect(() => {
    if (composition && selectedPrimary === null) {
      setSelectedPrimary(composition.primary_plan_id);
      setSelectedSecondaries(composition.secondary_plan_ids || []);
    }
  }, [composition, selectedPrimary]);

  // The selected primary's lineage determines which secondaries are eligible.
  const primaryDoc = useMemo(
    () => plans.find((p) => p.id === selectedPrimary) || null,
    [plans, selectedPrimary],
  );
  // When the user hasn't picked a primary plan yet (Free tier), we
  // still want to show the universal add-ons (WhatsApp por línea,
  // Reportes PDF) — they're compatible with every primary plan,
  // including the no-plan tier. Sentinel "free" never matches a real
  // lineage_id, so the backend filter returns only the addons that
  // have an empty `visible_in_primary_plans` (i.e. universal).
  const primaryLineage = primaryDoc?.lineage_id || primaryDoc?.id || "free";
  const isFreeViewing = !primaryDoc;

  const { data: secondariesData } = useQuery({
    queryKey: ["secondaries-for-primary", primaryLineage, activeCountry],
    queryFn: () => billingService.secondariesForPrimary(primaryLineage, activeCountry),
    enabled: !!primaryLineage,
  });
  const secondaries = useMemo(
    () => secondariesData?.secondaries || [],
    [secondariesData],
  );

  // Deep-link from UpsellModal: when `?highlight=<plan_id>` is present
  // we auto-select the plan (secondary → toggle on, primary → set as
  // primary), scroll the card into view, and ring it briefly. The query
  // param is cleared after first apply so a manual refresh won't fight
  // a user that since chose a different plan.
  //
  // NOTE: this effect is AFTER `secondaries` is declared on purpose —
  // earlier we hit a TDZ ReferenceError because `secondaries` was still
  // in its temporal-dead-zone when the effect closure was constructed.
  useEffect(() => {
    if (!highlightId || (plans.length === 0 && secondaries.length === 0)) return;
    const primaryMatch = plans.find((p) => p.id === highlightId);
    const secondaryMatch = secondaries.find((s) => s.id === highlightId);
    if (primaryMatch) {
      setSelectedPrimary(primaryMatch.id);
    } else if (secondaryMatch) {
      // For a secondary, ensure a primary is selected (use the one the
      // backend recommends — secondary has a primary_lineage_id). If
      // the user already has a composition, keep theirs.
      setSelectedSecondaries((cur) => cur.includes(secondaryMatch.id) ? cur : [...cur, secondaryMatch.id]);
    } else {
      // Plan not in this country's catalog (e.g., backend hint matched
      // an inactive/region-mismatched plan). Bail silently.
      return;
    }
    // Scroll + ring after the DOM has the card mounted.
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="pricing-plan-${highlightId}"], [data-testid="pricing-secondary-${highlightId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        highlightedRef.current = el;
      }
    });
    // Clear the ring after 3.5s and strip the query param so refresh
    // doesn't replay the animation.
    const t = setTimeout(() => {
      setHighlightedPlanId(null);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("highlight");
        return next;
      }, { replace: true });
    }, 3500);
    return () => clearTimeout(t);
  }, [highlightId, plans, secondaries]);

  // Auto-prune selected secondaries that no longer match the chosen primary.
  // Only mutate state when the filter actually changes the contents — without
  // this guard, the setState fires every render and causes an infinite loop.
  useEffect(() => {
    if (!primaryLineage) return;
    const validIds = new Set(secondaries.map((s) => s.id));
    setSelectedSecondaries((cur) => {
      const next = cur.filter((id) => validIds.has(id));
      return next.length === cur.length ? cur : next;
    });
  }, [primaryLineage, secondaries]);

  const errMessage = (e) => {
    const detail = e?.response?.data?.detail || "";
    const map = isEs ? {
      "no_active_composition_use_start": "No tienes una suscripción activa. Suscríbete primero.",
      "composition_already_active_use_update": "Ya tienes una suscripción activa. Usa 'Actualizar'.",
      "currency_change_not_supported_cancel_first": "Para cambiar de moneda, cancela tu suscripción primero.",
      "primary_plan_invalid_or_inactive": "El plan elegido no está activo.",
      "mp_create_failed": "El pago fue rechazado. Reintenta en unos segundos.",
      "mp_update_failed": "No se pudo actualizar la suscripción. Reintenta en unos segundos.",
    } : {
      "no_active_composition_use_start": "You don't have an active subscription. Subscribe first.",
      "composition_already_active_use_update": "You already have an active subscription. Use 'Update'.",
      "currency_change_not_supported_cancel_first": "To change currency, cancel your subscription first.",
      "primary_plan_invalid_or_inactive": "The selected plan is not active.",
      "mp_create_failed": "Payment was rejected. Retry in a moment.",
      "mp_update_failed": "Subscription update failed. Retry in a moment.",
    };
    if (map[detail]) return map[detail];
    if (typeof detail === "string" && detail.startsWith("amount_below_mp_minimum:")) {
      // detail = "amount_below_mp_minimum:COP:69900:min=200000:country=CO"
      const parts = detail.split(":");
      const cur = parts[1];
      const amt = parts[2];
      const min = (parts[3] || "min=0").replace("min=", "");
      const cc = (parts[4] || "country=??").replace("country=", "");
      return isEs
        ? `Tu plan suma ${cur} ${amt} pero MercadoPago ${cc} exige un mínimo de ${cur} ${min} por suscripción. Prueba un plan mayor, suma add-ons, o cambia tu país en Mi perfil.`
        : `Your plan totals ${cur} ${amt} but MercadoPago ${cc} requires a minimum of ${cur} ${min} per subscription. Try a higher plan, add some add-ons, or change your country in My profile.`;
    }
    if (typeof detail === "string" && detail.startsWith("mp_create_failed:")) {
      const mpMsg = detail.slice("mp_create_failed:".length);
      return isEs
        ? `El pago no se pudo procesar (${mpMsg}). Si persiste, escribime por chat.`
        : `Payment couldn't be processed (${mpMsg}). If it persists, message me on chat.`;
    }
    if (typeof detail === "string" && detail.startsWith("secondary_not_available_in_country")) {
      return isEs ? "Uno de los add-ons no está disponible en tu país." : "One of the add-ons is not available in your country.";
    }
    if (typeof detail === "string" && detail.startsWith("primary_plan_not_available_in_country")) {
      return isEs ? "El plan no está disponible en tu país." : "The plan is not available in your country.";
    }
    if (typeof detail === "string" && detail.startsWith("mp_timeout")) {
      return isEs
        ? "MercadoPago tardó demasiado en responder. Por favor reintentá en 1 minuto. Si el problema persiste, puede ser una caída temporal de MP: status.mercadopago.com"
        : "MercadoPago took too long to respond. Please retry in 1 minute. If the issue persists, MP may be down: status.mercadopago.com";
    }
    if (typeof detail === "string" && detail.startsWith("mp_sandbox_no_test_buyer:")) {
      // detail = "mp_sandbox_no_test_buyer:country=CO:hint=..."
      return isEs
        ? "MercadoPago está en modo Sandbox sin comprador de prueba configurado. El admin debe ir a Admin → Cobros → crear test buyer, o activar Producción."
        : "MercadoPago is in Sandbox mode with no test buyer configured. Admin must mint a test buyer in Admin → Cobros, or toggle Production.";
    }
    if (typeof detail === "string" && detail.startsWith("mp_error")) {
      return isEs ? `Error en el pago: ${detail.slice(9)}` : `Payment error: ${detail.slice(9)}`;
    }
    return detail || (isEs ? "Algo salió mal. Reintenta." : "Something went wrong. Retry.");
  };

  const start = useMutation({
    mutationFn: (body) => billingService.compositionStart(body),
    onSuccess: (r) => {
      if (r.init_point) {
        // Open MP in a popup + polling modal so the user never leaves
        // Lumfi. Falls back gracefully to a "open in new tab" link if
        // the browser blocks the popup. See MpCheckoutModal.
        setMpCheckout({
          initPoint: r.init_point,
          compositionId: r.composition_id,
        });
      }
    },
    onError: (e) => toast.error(errMessage(e)),
  });
  const update = useMutation({
    mutationFn: (body) => billingService.compositionUpdate(body),
    onSuccess: (r) => {
      if (r.one_off_init_point) {
        toast.success(isEs ? "Cargo de prorrateo generado." : "Proration charge generated.");
        // Upgrade with proration → MP one-shot Preference. Same modal
        // pattern; polling /composition/me works because the route
        // mutates the existing composition row on payment.
        setMpCheckout({
          initPoint: r.one_off_init_point,
          compositionId: r.composition_id,
        });
        return;
      }
      const msg = r.deferred_to_next_renewal
        ? (isEs
            ? `Cambio aplicado. ${r.proration_amount >= 0 ? "Diferencia" : "Crédito"} de ${formatMoney(Math.abs(r.proration_amount), r.currency)} se ajustará en tu próxima renovación.`
            : `Bundle updated. ${r.proration_amount >= 0 ? "Difference" : "Credit"} of ${formatMoney(Math.abs(r.proration_amount), r.currency)} will be applied on next renewal.`)
        : (isEs ? "Bundle actualizado." : "Bundle updated.");
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ["billing-me"] });
      qc.invalidateQueries({ queryKey: ["composition-me"] });
    },
    onError: (e) => toast.error(errMessage(e)),
  });
  const cancel = useMutation({
    mutationFn: () => billingService.compositionCancel(),
    onSuccess: () => {
      toast.success(isEs ? "Suscripción cancelada." : "Subscription cancelled.");
      qc.invalidateQueries({ queryKey: ["billing-me"] });
      qc.invalidateQueries({ queryKey: ["composition-me"] });
    },
    onError: (e) => toast.error(errMessage(e)),
  });
  const buyPack = (packId) => {
    // Bricks Orders flow — embedded checkout instead of MP-hosted redirect.
    // iter-170an — Thread the selected workspace into the URL so the
    // checkout screen can show "Para: <workspace>" and the backend
    // can associate the pack consumption to that space.
    const qs = selectedSpaceId && selectedSpaceId !== user?.id
      ? `?space_id=${encodeURIComponent(selectedSpaceId)}`
      : "";
    navigate(`/billing/pack-checkout/${packId}${qs}`);
  };

  const toggleSecondary = (id) => {
    setSelectedSecondaries((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  // Resolve the displayed price for any plan (primary OR add-on) given
  // the current billing period. For add-ons in annual mode: if the
  // add-on exposes annual_price_amount, use it; otherwise fall back to
  // monthly × 12 (full rate, no discount) — same rule the backend
  // applies in compute_composition_total.
  const priceForPlan = (p) => {
    const monthly = Number(p?.price_amount || 0);
    const currency = p?.currency || "USD";
    if (billingPeriod === "annual") {
      if (p?.annual_price_amount) {
        return {
          amount: Number(p.annual_price_amount),
          currency,
          monthly_amount: monthly,
          savings_pct: Number(p.annual_discount_pct || 0),
          has_annual: true,
          fallback: false,
        };
      }
      if (monthly > 0) {
        return {
          amount: round2(monthly * 12),
          currency,
          monthly_amount: monthly,
          savings_pct: 0,
          has_annual: false,
          fallback: true,  // addon-only fallback: monthly × 12, no discount
        };
      }
    }
    return { amount: monthly, currency, monthly_amount: monthly, savings_pct: 0, has_annual: !!p?.annual_price_amount, fallback: false };
  };
  function round2(n) { return Math.round(n * 100) / 100; }

  // Total of the currently composed bundle (local, optimistic).
  const computed = useMemo(() => {
    if (!primaryDoc) return null;
    const primaryPrice = priceForPlan(primaryDoc);
    const total = primaryPrice.amount
      + selectedSecondaries.reduce((acc, id) => {
        const s = secondaries.find((x) => x.id === id);
        if (!s) return acc;
        return acc + priceForPlan(s).amount;
      }, 0);
    return { total, currency: primaryPrice.currency };
  }, [primaryDoc, selectedSecondaries, secondaries, billingPeriod]);

  const submitCheckout = () => {
    // iter-170u — Primary plan is optional ONLY when the cart includes
    // at least one add-on. An empty cart still errors.
    const hasAnySecondary = (selectedSecondaries || []).length > 0;
    if (!selectedPrimary && !hasAnySecondary) {
      toast.error(isEs
        ? "Selecciona un plan o al menos un add-on"
        : "Select a plan or at least one add-on");
      return;
    }
    const body = {
      // Send null instead of an empty string so the backend's Optional
      // type accepts it cleanly. Pydantic coerces "" to None too, but
      // null is the clearer wire signal.
      primary_plan_id: selectedPrimary || null,
      secondary_plan_ids: selectedSecondaries,
      billing_period: billingPeriod,
      // iter-170an — Per-workspace billing. Backend defaults to the
      // user's personal space when ``space_id`` is null/omitted, so
      // single-workspace users keep the legacy flow untouched.
      space_id: selectedSpaceId || null,
    };
    if (hasComposition) update.mutate(body);
    else start.mutate(body);
  };

  // The bundle differs from what is currently active (only meaningful with composition).
  const bundleChanged = hasComposition && (
    selectedPrimary !== composition.primary_plan_id
    || JSON.stringify([...(selectedSecondaries || [])].sort()) !== JSON.stringify([...(composition.secondary_plan_ids || [])].sort())
    || (composition.billing_period || "monthly") !== billingPeriod
  );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6 fade-up" data-testid="pricing-page">
        <PageHeader
          testid="pricing-header"
          eyebrow={isEs ? "Suscripciones" : "Subscriptions"}
          title={isEs ? "Planes y packs" : "Plans & packs"}
          subtitle={isEs
            ? "Elige tu plan y, si quieres, suma add-ons. Pagas con UNA sola suscripción."
            : "Pick your plan and optionally add-ons. Pay with ONE subscription."}
          actions={
            supportedCodes.length > 1 ? (
              <div className="flex items-center gap-2">
                {profileCountry && (
                  <span
                    className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5"
                    title={isEs ? "País usado para cobrar tu suscripción" : "Country used to charge your subscription"}
                    data-testid="pricing-billing-zone"
                  >
                    {isEs ? "Zona de pago" : "Billing zone"}: {profileCountry}
                  </span>
                )}
                <div className="min-w-[140px]">
                  <Select value={activeCountry} onValueChange={setCountry}>
                    <SelectTrigger data-testid="pricing-country"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {supportedCodes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : null
          }
        />

        {/* iter-170an — Workspace selector (only with ≥2 workspaces).
            Defaults to the active workspace. The chosen value scopes
            both the composition payload AND the pack-checkout URL so
            the purchase associates with the workspace explicitly. */}
        {showSpaceSelector && (
          <Card
            className="p-3 rounded-2xl border-emerald-200 bg-emerald-50/40 flex items-center gap-3 flex-wrap"
            data-testid="pricing-space-selector"
          >
            <Users className="h-4 w-4 text-emerald-700 shrink-0" />
            <div className="text-xs text-emerald-900 flex-1 min-w-[180px]">
              <strong>{isEs ? "¿Para qué workspace?" : "Which workspace?"}</strong>
              <div className="mt-0.5 text-emerald-800/80">
                {isEs
                  ? "Esta compra se asociará al workspace seleccionado."
                  : "This purchase will be associated to the selected workspace."}
              </div>
            </div>
            <div className="min-w-[200px]">
              <Select
                value={selectedSpaceId || ""}
                onValueChange={(v) => setSelectedSpaceId(v)}
              >
                <SelectTrigger data-testid="pricing-space-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(spaces || []).map((s) => (
                    <SelectItem
                      key={s.id}
                      value={s.id}
                      data-testid={`pricing-space-option-${s.id}`}
                    >
                      {s.name}
                      {s.id === user?.id ? " · Personal" : ""}                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>
        )}

        {/* Country drift warning — when the user is browsing one
            country's prices but their backend profile.country is
            different. Without this, subscribing surprises them with a
            generic 502 because /composition/start uses
            profile.country (NOT the visual dropdown). */}
        {profileCountry && activeCountry !== profileCountry && (
          <Card className="p-3 rounded-2xl border-amber-300 bg-amber-50 flex items-start gap-2" data-testid="pricing-country-drift">
            <Info className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900 flex-1">
              <strong>
                {isEs
                  ? `Estás viendo precios en ${activeCountry} pero tu zona de pago es ${profileCountry}.`
                  : `You're previewing ${activeCountry} prices but your billing zone is ${profileCountry}.`}
              </strong>
              <div className="mt-0.5 text-amber-800">
                {isEs
                  ? `Al suscribirte cobraremos en la moneda de ${profileCountry}. Cambia tu país en `
                  : `Subscribing will charge in your ${profileCountry} currency. Change it in `}
                <a href="/profile" className="underline font-semibold" data-testid="pricing-go-profile">{isEs ? "Mi perfil" : "My profile"}</a>
                {isEs ? " si no es correcto." : " if this is wrong."}
              </div>
            </div>
          </Card>
        )}

        {/* Banner: plan cancelado + plan suplente */}
        {user && meBilling?.plan?.status === "cancelled" && (
          <Card className="p-4 rounded-2xl border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="pricing-cancelled-banner">
            <Sparkles className="h-5 w-5 text-amber-700 mt-0.5" />
            <div className="flex-1 text-sm text-amber-900">
              <strong>{isEs ? "Tu plan será descontinuado" : "Your plan is being discontinued"}</strong>
              <div className="mt-1">
                {isEs
                  ? `El plan ${meBilling.plan.name} será descontinuado el ${(meBilling.plan.cancelled_grace_until || "").slice(0,10)}.`
                  : `Plan ${meBilling.plan.name} will end on ${(meBilling.plan.cancelled_grace_until || "").slice(0,10)}.`}
                {meBilling.replacement_plan && (
                  <> {isEs
                    ? "Pasarás automáticamente a "
                    : "You'll be automatically moved to "}
                    <strong>{meBilling.replacement_plan.name}</strong>.
                  </>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Banner: composition pending — invite user to complete payment on MP */}
        {composition && composition.status === "pending" && composition.init_point && (
          <PendingPaymentBanner
            isEs={isEs}
            composition={composition}
            onResume={() => setMpCheckout({
              initPoint: composition.init_point,
              compositionId: composition.id,
            })}
          />
        )}

        {/* Current state — tu plan actual con consumo detallado */}
        {user && meBilling && (
          <CurrentPlanCard isEs={isEs} meBilling={meBilling} composition={composition} onCancel={() => cancel.mutate()} cancelling={cancel.isPending} />
        )}

        {/* Primaries grid — hidden entirely when there are no plans */}
        {!plansLoading && plans.length > 0 && (
        <div>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <h2 className="text-sm font-semibold text-slate-700">
              {isEs ? "1 · Elige tu plan" : "1 · Pick your plan"}
            </h2>
            <BillingPeriodToggle
              isEs={isEs}
              value={billingPeriod}
              onChange={setBillingPeriod}
              anyAnnual={plans.some((p) => p.annual_price_amount > 0)}
            />
          </div>
          {/* iter-169zs · Fase 3 — Mobile: scroll horizontal con snap
              (cada plan ocupa ~82% del viewport, los demás peek por la
              derecha para invitar al swipe). Desktop: grid 2/3 col
              comportamiento original. */}
          <div className="sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 lg:gap-8 sm:items-start pt-6
                          flex sm:flex gap-4 overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:snap-none
                          -mx-4 px-4 sm:mx-0 sm:px-0 pb-2 sm:pb-0"
               data-testid="pricing-plans-grid">
              {plans.map((p) => {
                const isSelected = p.id === selectedPrimary;
                const featureKeys = p.features || [];
                const resources = p.resources || {};
                const services = p.services || [];
                const priceInfo = priceForPlan(p);
                const showAnnual = billingPeriod === "annual" && priceInfo.has_annual;
                const isPopular = billingPeriod === "annual" ? !!p.popular_annual : !!p.popular_monthly;
                return (
                  <Card
                    key={p.id}
                    onClick={() => setSelectedPrimary(p.id)}
                    className={`relative p-6 lg:p-8 rounded-3xl transition-all duration-300 cursor-pointer flex flex-col h-full
                                shrink-0 w-[82vw] max-w-[340px] sm:w-auto sm:max-w-none snap-center ${
                      isPopular
                        ? "bg-slate-900 text-white border border-slate-800 shadow-2xl md:-translate-y-3 ring-1 ring-emerald-500/30 hover:md:-translate-y-4"
                        : isSelected
                          ? "bg-white border-2 border-emerald-500 ring-2 ring-emerald-100"
                          : "bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md"
                    } ${highlightedPlanId === p.id ? "ring-4 ring-amber-300 animate-pulse" : ""}`}
                    data-testid={`pricing-plan-${p.id}`}
                  >
                    {isPopular && (
                      <span
                        className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-[0.15em] shadow-lg whitespace-nowrap"
                        data-testid={`pricing-plan-${p.id}-popular`}
                      >
                        ★ {isEs ? "Más popular" : "Most popular"}
                      </span>
                    )}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <h3 className={`font-display text-lg font-bold ${isPopular ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
                        {p.version > 1 && <span className={`text-[10px] font-mono ${isPopular ? "text-slate-400" : "text-slate-400"}`}>v{p.version}</span>}
                      </div>
                      {isSelected && !isPopular && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{isEs ? "Elegido" : "Selected"}</Badge>}
                      {isSelected && isPopular && <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">{isEs ? "Elegido" : "Selected"}</Badge>}
                    </div>
                    <div className={`font-display text-4xl font-bold tabular-nums mt-2 ${isPopular ? "text-white" : "text-slate-900"}`} data-testid={`pricing-plan-${p.id}-price`}>
                      {formatMoney(priceInfo.amount, priceInfo.currency)}
                    </div>
                    <div className={`text-xs mb-2 ${isPopular ? "text-slate-400" : "text-slate-500"}`}>
                      {showAnnual
                        ? (isEs ? "por año" : "per year")
                        : (isEs
                          ? `por ${p.billing_interval_months === 1 ? "mes" : `${p.billing_interval_months} meses`}`
                          : `per ${p.billing_interval_months === 1 ? "month" : `${p.billing_interval_months} months`}`)}
                    </div>
                    {/* MP cadence hint — when the auto-picker bumped
                        the cadence to bi/tri/semestral because the
                        country's MP min sits above the monthly price,
                        we surface the actual charge schedule so the
                        user knows what their card will see. */}
                    {!showAnnual && p.billing_frequency_months > 1 && p.effective_charge_amount && (
                      <p
                        className="text-[10px] text-amber-700 italic mb-2"
                        data-testid={`pricing-plan-${p.id}-cadence`}
                        title={isEs
                          ? `MercadoPago ${activeCountry} exige un mínimo por suscripción mayor que tu precio mensual. Por eso lo cobramos cada ${p.billing_frequency_months} meses.`
                          : `MercadoPago ${activeCountry} enforces a per-subscription minimum higher than the monthly price. We bill every ${p.billing_frequency_months} months.`}
                      >
                        {isEs
                          ? `Cobro cada ${p.billing_frequency_months} meses (${formatMoney(p.effective_charge_amount, priceInfo.currency)})`
                          : `Charged every ${p.billing_frequency_months} months (${formatMoney(p.effective_charge_amount, priceInfo.currency)})`}
                      </p>
                    )}
                    {p.billing_unsellable && !showAnnual && (
                      <p
                        className="text-[10px] text-rose-700 italic mb-2"
                        data-testid={`pricing-plan-${p.id}-unsellable`}
                      >
                        {isEs
                          ? `No disponible en ${activeCountry}: el precio queda por debajo del mínimo MP en todas las cadencias.`
                          : `Unavailable in ${activeCountry}: monthly price stays below MP minimum even when stretched.`}
                      </p>
                    )}
                    {showAnnual && priceInfo.savings_pct > 0 && (
                      <Badge
                        className="mb-3 bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-300"
                        data-testid={`pricing-plan-${p.id}-savings`}
                      >
                        {isEs
                          ? `Ahorrás ${priceInfo.savings_pct}%`
                          : `Save ${priceInfo.savings_pct}%`}
                      </Badge>
                    )}
                    {billingPeriod === "annual" && !priceInfo.has_annual && (
                      <p className="text-[10px] text-amber-700 italic mb-3" data-testid={`pricing-plan-${p.id}-monthly-only`}>
                        {isEs
                          ? "Este plan solo está disponible mensual."
                          : "This plan is only available monthly."}
                      </p>
                    )}

                    {/* iter-169z3 — Curated marketing bullets when the
                        admin provided `display_features_i18n[lang]`.
                        These take FULL priority: we hide the auto-
                        generated Resources/Features/Services lists
                        because the admin already curated what to
                        highlight. When the field is empty we fall
                        back to the legacy three-section grid below. */}
                    {(() => {
                      const adminBullets = (p.display_features_i18n?.[lang] || []).slice(0, 8);
                      if (adminBullets.length === 0) return null;
                      return (
                        <ul
                          className={`text-sm space-y-1.5 mb-3 ${isPopular ? "text-slate-300" : "text-slate-700"}`}
                          data-testid={`pricing-plan-${p.id}-curated-bullets`}
                        >
                          {adminBullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${isPopular ? "text-emerald-400" : "text-emerald-500"}`} />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}

                    {/* Fallback grid — only when there are NO curated
                        bullets. Keeps the in-app pricing honest for
                        plans that haven't been polished yet. */}
                    {!(p.display_features_i18n?.[lang] || []).length && Object.keys(resources).length > 0 && (
                      <div className="mb-3 space-y-1" data-testid={`pricing-plan-${p.id}-resources`}>
                        <div className={`text-[10px] uppercase tracking-wider font-semibold ${isPopular ? "text-slate-400" : "text-slate-400"}`}>{isEs ? "Recursos" : "Resources"}</div>
                        {Object.entries(resources).map(([k, v]) => {
                          const meta = RESOURCE_LABELS[k] || { es: k, en: k, unit: "" };
                          const display = v === 0 ? (isEs ? "Ilimitado" : "Unlimited") : `${v} ${meta.unit}`.trim();
                          return (
                            <div key={k} className={`text-xs flex justify-between ${isPopular ? "text-slate-300" : "text-slate-600"}`}>
                              <span>{meta[isEs ? "es" : "en"]}</span>
                              <span className="font-semibold tabular-nums">{display}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {!(p.display_features_i18n?.[lang] || []).length && featureKeys.length > 0 && (
                      <div className="mb-3">
                        <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${isPopular ? "text-slate-400" : "text-slate-400"}`}>{isEs ? "Funciones" : "Features"}</div>
                        <ul className={`text-sm space-y-1 ${isPopular ? "text-slate-300" : "text-slate-600"}`}>
                          {featureKeys.map((k) => (
                            <li key={k} className="flex items-start gap-2">
                              <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${isPopular ? "text-emerald-400" : "text-emerald-500"}`} />
                              <span>{FEATURE_LABELS[k]?.[isEs ? "es" : "en"] || k}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {!(p.display_features_i18n?.[lang] || []).length && services.length > 0 && (
                      <div className="mb-1">
                        <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${isPopular ? "text-slate-400" : "text-slate-400"}`}>{isEs ? "Servicios" : "Services"}</div>
                        <ul className={`text-xs space-y-0.5 ${isPopular ? "text-slate-300" : "text-slate-600"}`}>
                          {services.map((k) => (
                            <li key={k} className="flex items-center gap-1.5">
                              <span className={`h-1 w-1 rounded-full ${isPopular ? "bg-sky-400" : "bg-sky-500"}`} />
                              {SERVICE_LABELS[k]?.[isEs ? "es" : "en"] || k}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
            <div
              className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-xs text-slate-500"
              data-testid="pricing-trust-signals"
            >
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                {isEs ? "Pago seguro vía MercadoPago" : "Secure payment via MercadoPago"}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-emerald-600" />
                {isEs ? "Cancelá cuando quieras" : "Cancel anytime"}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
                {isEs ? "Sin renovaciones sorpresa" : "No surprise renewals"}
              </span>
            </div>
        </div>
        )}

        {/* Secondaries — visible when there are universal add-ons compatible
            with the current view. Add-ons in annual mode use their own
            annual price if configured, otherwise monthly × 12 (full
            rate, no discount). A small chip per card explains the
            fallback when used. */}
        {secondaries.length > 0 && (
          <div data-testid="pricing-secondaries-section">
            <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2 flex-wrap">
              <Layers className="h-4 w-4" />
              {isEs ? "2 · Add-ons (opcional)" : "2 · Add-ons (optional)"}
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-600 bg-slate-100 rounded-full px-2 py-0.5"
                data-testid="pricing-addons-currency-label"
                title={isEs
                  ? "Add-ons mostrados en la moneda de tu zona de pago"
                  : "Add-ons priced in your billing zone currency"}
              >
                {(secondaries[0]?.currency || "—")} · {activeCountry}
              </span>
              {isFreeViewing && (
                <span className="text-[10px] uppercase tracking-wider font-bold text-sky-700 bg-sky-100 rounded-full px-2 py-0.5">
                  {isEs ? "Compra add-ons solos o con plan" : "Add-ons can be purchased standalone"}
                </span>
              )}
              {billingPeriod === "annual" && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-sky-800 bg-sky-100 rounded-full px-2 py-0.5"
                  title={isEs
                    ? "Los add-ons sin precio anual configurado se cobran a tarifa mensual × 12."
                    : "Add-ons without annual pricing are charged at monthly × 12."}
                  data-testid="pricing-addons-annual-mode"
                >
                  <Info className="h-3 w-3" />
                  {isEs ? "Tarifa anual" : "Annual rate"}
                </span>
              )}
            </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {secondaries.map((s) => {
                  const selected = selectedSecondaries.includes(s.id);
                  const sPrice = priceForPlan(s);
                  return (
                    <Card
                      key={s.id}
                      onClick={() => toggleSecondary(s.id)}
                      className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${selected ? "border-sky-500 bg-sky-50/40" : "border-slate-200 hover:border-slate-300"} ${highlightedPlanId === s.id ? "ring-4 ring-amber-300 animate-pulse" : ""}`}
                      data-testid={`pricing-secondary-${s.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-display text-base font-bold text-slate-900">{s.name}</h3>
                        {selected
                          ? <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">✓</Badge>
                          : <Plus className="h-4 w-4 text-slate-400" />}
                      </div>
                      <div className="font-display text-2xl font-bold tabular-nums text-slate-900" data-testid={`pricing-secondary-${s.id}-price`}>
                        +{formatMoney(sPrice.amount, sPrice.currency)}
                      </div>
                      <div className="text-[10px] text-slate-500 mb-2">
                        {billingPeriod === "annual"
                          ? (isEs ? "por año" : "per year")
                          : (isEs ? "por mes" : "per month")}
                      </div>
                      {billingPeriod === "annual" && sPrice.fallback && (
                        <span
                          className="inline-block mb-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                          title={isEs
                            ? "Este add-on no tiene precio anual; se cobra a tarifa mensual × 12."
                            : "This add-on has no annual price; charged at monthly × 12."}
                          data-testid={`pricing-secondary-${s.id}-fallback`}
                        >
                          {isEs ? "tarifa plena × 12" : "full rate × 12"}
                        </span>
                      )}
                      {billingPeriod === "annual" && !sPrice.fallback && sPrice.savings_pct > 0 && (
                        <Badge className="mb-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-300" data-testid={`pricing-secondary-${s.id}-savings`}>
                          {isEs ? `Ahorrás ${sPrice.savings_pct}%` : `Save ${sPrice.savings_pct}%`}
                        </Badge>
                      )}
                      {/* iter-169z6 — same fallback as primary plans:
                          curated `display_features_i18n[lang]` wins
                          over the auto-generated feature list. */}
                      {(() => {
                        const adminBullets = (s.display_features_i18n?.[lang] || []).slice(0, 6);
                        if (adminBullets.length > 0) {
                          return (
                            <ul
                              className="text-xs text-slate-600 space-y-0.5"
                              data-testid={`pricing-addon-${s.id}-curated-bullets`}
                            >
                              {adminBullets.map((b, i) => (
                                <li key={i} className="flex items-start gap-1.5">
                                  <CheckCircle2 className="h-3 w-3 text-sky-500 mt-0.5 shrink-0" />
                                  <span>{b}</span>
                                </li>
                              ))}
                            </ul>
                          );
                        }
                        return (s.features || []).length > 0 ? (
                          <ul className="text-xs text-slate-600 space-y-0.5">
                            {(s.features || []).map((k) => (
                              <li key={k} className="flex items-center gap-1.5">
                                <CheckCircle2 className="h-3 w-3 text-sky-500" />
                                {FEATURE_LABELS[k]?.[isEs ? "es" : "en"] || k}
                              </li>
                            ))}
                          </ul>
                        ) : null;
                      })()}
                    </Card>
                  );
                })}
              </div>
          </div>
        )}

        {/* Checkout summary + CTA — iter-170u: shows when EITHER a
            primary is selected OR there's at least one secondary in
            the cart (addons-standalone). */}
        {(selectedPrimary || selectedSecondaries.length > 0) && (
          <Card className="p-5 rounded-2xl border-emerald-200 bg-emerald-50/60 space-y-3" data-testid="pricing-summary">
            <h2 className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              {hasComposition
                ? (isEs ? "Actualizar tu suscripción" : "Update your subscription")
                : (isEs ? "Resumen y suscripción" : "Summary & checkout")}
              <Badge
                className={
                  billingPeriod === "annual"
                    ? "bg-sky-100 text-sky-800 hover:bg-sky-100 border border-sky-300"
                    : "bg-sky-100 text-sky-800 hover:bg-sky-100 border border-sky-200"
                }
                data-testid="pricing-summary-period"
              >
                {billingPeriod === "annual"
                  ? (isEs ? "Anual" : "Annual")
                  : (isEs ? "Mensual" : "Monthly")}
              </Badge>
              {showSpaceSelector && selectedSpace && (
                <Badge
                  className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200"
                  data-testid="pricing-summary-space"
                  title={isEs ? "Workspace destino" : "Target workspace"}
                >
                  <Users className="h-3 w-3 mr-1" />
                  {selectedSpace.name}
                </Badge>
              )}
            </h2>
            <div className="space-y-1 text-sm">
              {primaryDoc && (() => {
                const pi = priceForPlan(primaryDoc);
                return (
                  <div className="flex justify-between" data-testid="summary-primary">
                    <span className="text-slate-700">{primaryDoc.name} <Badge variant="outline" className="ml-1 text-[10px]">{isEs ? "Plan" : "Plan"}</Badge></span>
                    <span className="font-semibold tabular-nums">{formatMoney(pi.amount, pi.currency)}</span>
                  </div>
                );
              })()}
              {!primaryDoc && selectedSecondaries.length > 0 && (
                <div className="text-xs text-slate-500 italic" data-testid="summary-addons-only-note">
                  {isEs
                    ? "Compra de add-ons sin plan base — puedes sumar un plan más tarde."
                    : "Add-on-only purchase — you can add a base plan later."}
                </div>
              )}
              {selectedSecondaries.map((id) => {
                const s = secondaries.find((x) => x.id === id);
                if (!s) return null;
                const sPrice = priceForPlan(s);
                return (
                  <div key={id} className="flex justify-between text-slate-600" data-testid={`summary-secondary-${id}`}>
                    <span>
                      {s.name}
                      <Badge variant="outline" className="ml-1 text-[10px] bg-sky-50">{isEs ? "Add-on" : "Add-on"}</Badge>
                      {billingPeriod === "annual" && sPrice.fallback && (
                        <span className="ml-1 text-[10px] text-amber-700 italic">({isEs ? "tarifa plena × 12" : "full rate × 12"})</span>
                      )}
                    </span>
                    <span className="font-semibold tabular-nums">+{formatMoney(sPrice.amount, sPrice.currency)}</span>
                  </div>
                );
              })}
              <div className="border-t border-emerald-200 pt-2 flex justify-between text-base font-bold text-slate-900" data-testid="summary-total">
                <span>
                  {billingPeriod === "annual"
                    ? (isEs ? "Total anual" : "Annual total")
                    : (isEs ? "Total recurrente" : "Recurring total")}
                </span>
                <span className="font-display tabular-nums">{computed && formatMoney(computed.total, computed.currency)}</span>
              </div>
              {billingPeriod === "annual" && primaryDoc?.annual_discount_pct > 0 && (
                <p className="text-xs text-emerald-700" data-testid="summary-savings-note">
                  {isEs
                    ? `Ahorrás ${primaryDoc.annual_discount_pct}% vs. pago mensual (${formatMoney((primaryDoc.price_amount || 0) * 12, primaryDoc.currency)}/año).`
                    : `You save ${primaryDoc.annual_discount_pct}% vs. monthly billing (${formatMoney((primaryDoc.price_amount || 0) * 12, primaryDoc.currency)}/yr).`}
                </p>
              )}
            </div>
            {hasComposition && bundleChanged && (
              <p className="text-xs text-slate-600" data-testid="summary-proration-note">
                {isEs
                  ? "Si la diferencia supera el umbral de prorrateo (40.000 COP por defecto), te cobraremos al instante el ajuste proporcional. Si es menor, se difiere a tu próxima renovación."
                  : "If the difference exceeds the proration threshold (default 40,000 COP), the prorated charge runs immediately. Otherwise it's deferred to your next renewal."}
              </p>
            )}
            <Button
              className="w-full"
              data-testid="pricing-checkout"
              disabled={start.isPending || update.isPending || (hasComposition && !bundleChanged)}
              onClick={submitCheckout}
            >
              {hasComposition
                ? (bundleChanged
                    ? (isEs ? "Actualizar suscripción" : "Update subscription")
                    : (isEs ? "Sin cambios" : "No changes"))
                : (isEs ? "Suscribirme" : "Subscribe")}
            </Button>
          </Card>
        )}

        {/* Packs grid — only when there are active packs */}
        {!packsLoading && packs.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Package2 className="h-4 w-4" />
            {isEs ? "Packs (one-time)" : "Packs (one-time)"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {packs.map((p) => {
                // iter-169w — packs now expose a generic `units` count
                // (legacy `ai_credits` kept for backwards compat). Hide
                // the line entirely when neither is set so non-metered
                // packs (e.g. a one-off service) read cleanly.
                const units = p.units ?? p.ai_credits ?? 0;
                return (
                <Card key={p.id} className="p-4 rounded-2xl border-slate-200" data-testid={`pricing-pack-${p.id}`}>
                  <div className="font-semibold text-slate-900">{p.name}</div>
                  <div className="font-display text-xl font-bold mt-1">{formatMoney(p.price_amount, p.currency)}</div>
                  {units > 0 && (
                    <div className="text-xs text-slate-500 mb-3">{units} {isEs ? "unidades" : "units"}</div>
                  )}
                  <Button
                    variant="outline"
                    className="w-full"
                    data-testid={`pricing-buy-${p.id}`}
                    onClick={() => buyPack(p.id)}
                  >
                    {isEs ? "Comprar" : "Buy"}
                  </Button>
                </Card>
              );})}
            </div>
        </div>
        )}

        <Card className="p-4 rounded-2xl border-slate-200 bg-slate-50/40">
          <p className="text-xs text-slate-600">
            {isEs
              ? "Un solo cobro recurrente que suma tu plan + add-ons. Pago seguro · cancela cuando quieras."
              : "ONE consolidated recurring charge for your plan + add-ons. Secure payment · cancel anytime."}
          </p>
        </Card>

        <PricingFeatureMatrix lang={lang} />
      </div>

      <MpCheckoutModal
        open={Boolean(mpCheckout)}
        initPoint={mpCheckout?.initPoint}
        compositionId={mpCheckout?.compositionId}
        isEs={isEs}
        returnUrl="/payments/return?type=composition"
        onClose={() => setMpCheckout(null)}
        onSuccess={() => {
          setMpCheckout(null);
          qc.invalidateQueries({ queryKey: ["billing-me"] });
          qc.invalidateQueries({ queryKey: ["composition-me"] });
          navigate("/payments/return?type=composition&status=success");
        }}
      />
    </Layout>
  );
}


/**
 * BillingPeriodToggle — pill switch above the plans grid.
 *
 * Sticky across /pricing ↔ /billing via the localStorage key
 * "lumfi.billing_period.v1" managed by the parent. Disables the
 * "Anual" arm when no plan in the current country exposes an annual
 * price (admin hasn't configured it) so the toggle never lies about
 * what's actually available.
 */
function BillingPeriodToggle({ isEs, value, onChange, anyAnnual }) {
  return (
    <div
      className="inline-flex items-center bg-slate-100 rounded-full p-0.5 border border-slate-200"
      data-testid="billing-period-toggle"
      role="tablist"
      aria-label={isEs ? "Período de facturación" : "Billing period"}
    >
      {[
        { v: "monthly", labelEs: "Mensual",  labelEn: "Monthly" },
        { v: "annual",  labelEs: "Anual",    labelEn: "Annual" },
      ].map((opt) => {
        const disabled = opt.v === "annual" && !anyAnnual;
        const isActive = value === opt.v;
        return (
          <button /* design-system: ALLOW legacy-todo */
            key={opt.v}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={disabled}
            onClick={() => !disabled && onChange(opt.v)}
            data-testid={`billing-period-${opt.v}`}
            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all ${
              isActive
                ? "bg-white text-slate-900 shadow-sm"
                : disabled
                  ? "text-slate-300 cursor-not-allowed"
                  : "text-slate-600 hover:text-slate-900"
            }`}
            title={disabled
              ? (isEs ? "Ningún plan tiene precio anual configurado" : "No plan exposes annual pricing yet")
              : undefined}
          >
            {isEs ? opt.labelEs : opt.labelEn}
            {opt.v === "annual" && anyAnnual && (
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-700">
                {isEs ? "AHORRO" : "SAVE"}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}


function PendingPaymentBanner({ isEs, composition, onResume }) {
  const qc = useQueryClient();
  const cancelPending = useMutation({
    mutationFn: () => billingService.compositionCancelPending(),
    onSuccess: () => {
      toast.success(isEs ? "Pago pendiente cancelado. Puedes empezar de nuevo." : "Pending payment cancelled. You can start over.");
      qc.invalidateQueries({ queryKey: ["billing-me"] });
      qc.invalidateQueries({ queryKey: ["composition-me"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "error"),
  });

  const createdAt = composition?.created_at;
  // iter-169zw · Use a ticking `now` state so the elapsed/remaining time
  // refresh visually each minute. Calling `Date.now()` directly inside a
  // `useMemo` is impure render — React's purity rule flags it because
  // the result depends on wall-clock time. Pinning the timestamp in state
  // + a `setInterval` keeps the calculation deterministic per render.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const elapsed = useMemo(() => {
    if (!createdAt) return null;
    try {
      const created = new Date(createdAt);
      const diffMs = now - created.getTime();
      const totalMin = Math.max(0, Math.floor(diffMs / 60000));
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      // Pending TTL is 23h — backend cron expires at 23h.
      const remainingMin = Math.max(0, 23 * 60 - totalMin);
      const rh = Math.floor(remainingMin / 60);
      const rm = remainingMin % 60;
      return { h, m, totalMin, rh, rm };
    } catch (_e) {
      return null;
    }
  }, [createdAt]);

  const elapsedLabel = elapsed
    ? (elapsed.h > 0
        ? (isEs ? `${elapsed.h}h ${elapsed.m}m` : `${elapsed.h}h ${elapsed.m}m`)
        : (isEs ? `${elapsed.m} min` : `${elapsed.m} min`))
    : "—";
  const remainingLabel = elapsed
    ? (elapsed.rh > 0
        ? (isEs ? `${elapsed.rh}h ${elapsed.rm}m` : `${elapsed.rh}h ${elapsed.rm}m`)
        : (isEs ? `${elapsed.rm} min` : `${elapsed.rm} min`))
    : "—";

  return (
    <Card className="p-4 rounded-2xl border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="pricing-pending-banner">
      <CreditCard className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-sm text-amber-900">
        <strong>{isEs ? "Pago pendiente de confirmación" : "Payment pending confirmation"}</strong>
        <div className="mt-1">
          {isEs
            ? "Tu suscripción aún no está activa. Completa el pago para empezar a usar el plan elegido."
            : "Your subscription isn't active yet. Complete the payment to start using the chosen plan."}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-amber-800" data-testid="pending-elapsed">
          <span><strong>{isEs ? "Pendiente hace:" : "Pending for:"}</strong> {elapsedLabel}</span>
          <span><strong>{isEs ? "Expira en:" : "Expires in:"}</strong> {remainingLabel}</span>
        </div>
      </div>
      <div className="flex flex-col gap-2 items-stretch">
        <Button
          size="sm"
          data-testid="pricing-pending-retry"
          onClick={onResume}
          className="bg-amber-600 hover:bg-amber-700"
        >
          {isEs ? "Completar pago" : "Complete payment"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          data-testid="pricing-pending-cancel"
          disabled={cancelPending.isPending}
          onClick={() => cancelPending.mutate()}
          className="border-amber-300 text-amber-800 hover:bg-amber-100"
        >
          {isEs ? "Cancelar pago pendiente" : "Cancel pending"}
        </Button>
      </div>
    </Card>
  );
}


function CurrentPlanCard({ isEs, meBilling, composition, onCancel, cancelling }) {
  const plan = meBilling.plan;
  const usage = meBilling.usage || {};
  const sub = meBilling.subscription;
  const allotment = usage.ai_messages_allotment || 0;
  const used = usage.ai_messages_used || 0;
  const remaining = usage.ai_messages_remaining;
  const packBalance = usage.pack_balance || 0;
  const daysLeft = usage.days_remaining_in_period;
  const periodLabel = usage.period_key || "";

  const pct = allotment > 0 ? Math.min(100, Math.round((used / allotment) * 100)) : null;
  const barColor = pct === null
    ? "bg-slate-300"
    : pct < 60 ? "bg-emerald-500"
    : pct < 90 ? "bg-amber-500"
    : "bg-red-500";

  return (
    <Card className="p-5 rounded-2xl border-slate-200 space-y-3" data-testid="pricing-current">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-emerald-600" />
          <h2 className="font-semibold text-slate-800">{isEs ? "Tu plan actual" : "Your current plan"}</h2>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {plan ? `${plan.name}${plan.version > 1 ? ` v${plan.version}` : ""}` : (isEs ? "Free" : "Free")}
            {sub?.status && sub.status !== "active" && <> · {sub.status}</>}
          </Badge>
          {composition && composition.status === "active" && (
            <Button variant="outline" size="sm" onClick={onCancel} disabled={cancelling} data-testid="pricing-cancel-subscription" className="text-red-600 border-red-200 hover:bg-red-50">
              {isEs ? "Cancelar suscripción" : "Cancel subscription"}
            </Button>
          )}
        </div>
      </div>

      {composition && (composition.breakdown || []).length > 0 && (
        <div className="space-y-1 text-xs bg-slate-50/60 rounded-lg p-3" data-testid="pricing-breakdown">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
            {isEs ? "Composición actual" : "Current bundle"}
          </div>
          {composition.breakdown.map((b) => (
            <div key={b.plan_id} className="flex justify-between" data-testid={`breakdown-${b.plan_id}`}>
              <span>
                {b.name}
                <Badge variant="outline" className="ml-1 text-[10px]">
                  {b.kind === "primary" ? (isEs ? "Plan" : "Plan") : (isEs ? "Add-on" : "Add-on")}
                </Badge>
              </span>
              <span className="font-semibold tabular-nums">{formatMoney(b.amount, composition.currency)}</span>
            </div>
          ))}
          {(composition.pending_proration_amount || 0) !== 0 && (
            <div className="flex justify-between border-t border-slate-200 pt-1 mt-1 text-amber-700" data-testid="pricing-pending-proration">
              <span>{isEs ? "Ajuste prorrateado en próxima renovación" : "Deferred proration on next renewal"}</span>
              <span className="font-semibold tabular-nums">{formatMoney(composition.pending_proration_amount, composition.currency)}</span>
            </div>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3" data-testid="pricing-usage">
        <div className="space-y-1">
          <div className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1">
            <MessageSquare className="h-3 w-3" />
            {isEs ? "Mensajes IA" : "AI messages"}
          </div>
          <div className="text-lg font-bold tabular-nums text-slate-800">
            {used}
            {allotment > 0 && <span className="text-sm font-normal text-slate-500"> / {allotment}</span>}
          </div>
          {pct !== null && (
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-testid="pricing-usage-bar">
              <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
            </div>
          )}
          <div className="text-[10px] text-slate-400">
            {allotment > 0
              ? (isEs ? `${remaining ?? 0} restantes este mes` : `${remaining ?? 0} left this month`)
              : (isEs ? "Free tier · cap diario" : "Free tier · daily cap")}
          </div>
        </div>

        <div className="space-y-1">
          <div className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1">
            <Package2 className="h-3 w-3" />
            {isEs ? "Saldo de packs" : "Pack balance"}
          </div>
          <div className="text-lg font-bold tabular-nums text-slate-800" data-testid="pricing-pack-balance">
            {packBalance}
          </div>
          <div className="text-[10px] text-slate-400">
            {isEs ? "Unidades extras desde packs" : "Extra pack units"}
          </div>
        </div>

        <div className="space-y-1">
          <div className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {isEs ? "Periodo actual" : "Current period"}
          </div>
          <div className="text-lg font-bold tabular-nums text-slate-800" data-testid="pricing-days-left">
            {daysLeft !== null && daysLeft !== undefined
              ? <>{daysLeft} <span className="text-sm font-normal text-slate-500">{isEs ? "días" : "days"}</span></>
              : "—"}
          </div>
          <div className="text-[10px] text-slate-400">{periodLabel}</div>
        </div>
      </div>

      {pct !== null && pct >= 80 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="pricing-usage-warning">
          <Gauge className="h-4 w-4" />
          {isEs
            ? "Estás cerca del límite mensual. Considera comprar un pack o pasar a un plan mayor."
            : "You're close to the monthly limit. Consider buying a pack or upgrading."}
        </div>
      )}
    </Card>
  );
}
